import first from "lodash/first";
import isNil from "lodash/isNil";

import { PickUpVariant, PromoCode, Stop } from "types";
import { renderBaseRateFieldLabel } from "../../components/price-summary/utils/displayUtils";

export type UseOrderPricingType = {
  baseRate: {
    label: string;
    value: number;
  };
  driverGratuity: {
    label: string;
    value: number;
  };
  promoDiscount: {
    label: string;
    value: number;
  };
  tax: {
    label: string;
    value: number;
  };
  tolls: {
    label: string;
    value: number;
  };
  meetGreet: {
    label: string;
    value: number;
    selected: boolean;
  };
  boosterSeat: {
    label: string;
    value: number;
    quantity?: number;
  };
  forwardFacingSeat: {
    label: string;
    value: number;
    quantity?: number;
  };
  rearFacingSeat: {
    label: string;
    value: number;
    quantity?: number;
  };
  other: {
    label: string;
    value: number;
  };
  other2: {
    label: string;
    value: number;
  };
  other3: {
    label: string;
    value: number;
  };
  other4: {
    label: string;
    value: number;
  };
  other5: {
    label: string;
    value: number;
  };
  other6: {
    label: string;
    value: number;
  };
  stc: {
    label: string;
    value: number;
  };
  creditCardServiceFee: {
    label: string;
    value: number;
  };
  promoCode?: {
    label: string;
    value: number;
    removeField?: string;
  };
};

export type CustomerPricingType = {
  outboundTripPricing: UseOrderPricingType;
  outboundTripTotalAmt: number;
  returnTripPricing?: UseOrderPricingType;
  returnTripTotalAmt?: number;
  orderTotalAmt: number;
  tripIndex?: number;
};

export type PricingByTripType = {
  tripId: string;
  returnTripPricing?: PricingByTripType;
} & CustomerPricingType;

export type CalculateTripPricingProps = {
  trip: any;
  userSelectedDriverGratuityPercent?: number;
  shouldRenderDriverGratuity: boolean;
  pickUpVariantSelected: PickUpVariant;
  pricingLayoutMeetGreet: number;
  userAppliedPromoCode?: Pick<
    PromoCode,
    "id" | "promoCodeName" | "promoCodeAmt" | "promoCodePercent"
  >;
  childSeatInput?: {
    boosterSeatAmt?: number;
    boosterSeatQuantity?: number;
    forwardFacingSeatAmt?: number;
    forwardFacingSeatQuantity?: number;
    rearFacingSeatAmt?: number;
    rearFacingSeatQuantity?: number;
  };
  currencyFormatter: (amount: number) => string;
  calculatedOnSubtotalItems?: string[];
};

type CalculatePricingParams = {
  percent?: number;
  amt?: number;
  baseRateAmt: number;
};

export const calculatePricing = (params: CalculatePricingParams) => {
  const { percent, amt, baseRateAmt } = params;

  // TODO: consider letting customer know if value was calculated as a percentage.
  // right now just shows total amt.
  if (!amt && !percent) return 0;
  return percent * baseRateAmt || amt;
};

export const calculateTripPricing = (props: CalculateTripPricingProps) => {
  const {
    trip,
    shouldRenderDriverGratuity,
    userSelectedDriverGratuityPercent,
    pickUpVariantSelected,
    pricingLayoutMeetGreet,
    userAppliedPromoCode,
    currencyFormatter,
    childSeatInput: {
      boosterSeatAmt = null,
      boosterSeatQuantity,
      forwardFacingSeatAmt = null,
      forwardFacingSeatQuantity,
      rearFacingSeatAmt = null,
      rearFacingSeatQuantity,
    } = {},
    calculatedOnSubtotalItems = [],
  } = props;

  const {
    baseRateAmt,
    hourlyBaseRate,
    hourlyBaseRateHours,
    driverGratuityAmt,
    driverGratuityPercent,
    promoDiscountAmt,
    promoDiscountPercent,
    taxAmt,
    taxPercent,
    tollsAmt,
    meetGreetAmt: operatorSetMeetGreetAmt,
    otherAmt,
    otherPercent,
    otherName,
    other2Amt,
    other2Percent,
    other2Name,
    other3Amt,
    other3Percent,
    other3Name,
    other4Amt,
    other4Percent,
    other4Name,
    other5Amt,
    other5Percent,
    other5Name,
    other6Amt,
    other6Percent,
    other6Name,
    stcAmt,
    stcPercent,
    creditCardServiceFeeAmt,
    creditCardServiceFeePercent,
    promoCodeId,
    promoCodeName,
    promoCodeAmt,
    promoCodePercent,
  } = trip.routes[0].pricing || {};

  const convertNaN = (value) => {
    return isNaN(value)
      ? 0
      : !isNil(value)
      ? Math.round(value * 100) / 100
      : null;
  };

  const hasUserSelectedDriverGratuity =
    shouldRenderDriverGratuity && !isNil(userSelectedDriverGratuityPercent);

  const normalizedDriverGratuityParams: { percent?: number; amt?: number } =
    hasUserSelectedDriverGratuity
      ? { percent: userSelectedDriverGratuityPercent / 100 }
      : !isNil(driverGratuityAmt) || !isNil(driverGratuityPercent)
      ? { percent: driverGratuityPercent, amt: driverGratuityAmt }
      : shouldRenderDriverGratuity
      ? { percent: userSelectedDriverGratuityPercent / 100 }
      : { amt: 0 };

  let meetGreetAmtInput;
  let meetGreetSelected;
  const isAirportPickup = !!first<Stop>(trip.stops).airport?.icaoCode;

  if (!isNil(operatorSetMeetGreetAmt)) {
    // if operator already set meet & greet amt
    meetGreetAmtInput = operatorSetMeetGreetAmt;
    meetGreetSelected = true;
  } else if (isAirportPickup) {
    // if trip has an aiport pick up stop
    meetGreetAmtInput =
      pickUpVariantSelected === PickUpVariant.MeetGreet
        ? pricingLayoutMeetGreet
        : null;
    meetGreetSelected = pickUpVariantSelected === PickUpVariant.MeetGreet;
  } else {
    // if trip does not have an airport pick up stop
    meetGreetAmtInput = null;
    meetGreetSelected = false;
  }

  // use promo code if it exists on trip, else use promo code from user applied
  const promoCode = promoCodeId
    ? {
        label: `Promo Code (${promoCodeName})`,
        value: convertNaN(
          !isNil(promoCodePercent)
            ? promoCodePercent * baseRateAmt
            : promoCodeAmt
        ),
        isNeg: true,
      }
    : userAppliedPromoCode
    ? {
        label: `Promo Code (${userAppliedPromoCode.promoCodeName})`,
        value: convertNaN(
          !isNil(userAppliedPromoCode.promoCodePercent)
            ? userAppliedPromoCode.promoCodePercent * baseRateAmt
            : userAppliedPromoCode.promoCodeAmt
        ),
        isNeg: true,
        removeField: "promoCodeCustomerInput",
      }
    : {
        label: "Promo Code",
        value: null,
      };

  // Determine which items are calculated on subtotal vs base rate
  const calcOnSubtotalSet = new Set(calculatedOnSubtotalItems);
  const hasCalcOnSubtotal = calcOnSubtotalSet.size > 0;

  // Helper: resolve a percent-based item using the appropriate base
  const resolvePercentItem = (
    percent: number | null | undefined,
    amt: number | null | undefined,
    base: number
  ) => {
    return percent * base || amt;
  };

  // Existing quotes should honor persisted route amounts for operator-set items.
  // If an amount is missing, fall back to the percent-based calculation path.
  const resolveStoredOrFirstPassItem = (
    fieldName: string,
    percent: number | null | undefined,
    amt: number | null | undefined
  ) => {
    if (!isNil(amt)) return convertNaN(amt);

    return !calcOnSubtotalSet.has(fieldName)
      ? convertNaN(resolvePercentItem(percent, amt, baseRateAmt))
      : null;
  };

  const resolveStoredOrFinalItem = (
    fieldName: string,
    percent: number | null | undefined,
    amt: number | null | undefined,
    firstPassValue: number | null
  ) => {
    if (!isNil(amt)) return convertNaN(amt);

    return calcOnSubtotalSet.has(fieldName)
      ? convertNaN(resolvePercentItem(percent, amt, subtotalAmt))
      : firstPassValue;
  };

  // First pass: resolve subtotal-group items using baseRateAmt
  const driverGratuityValueFirstPass = !calcOnSubtotalSet.has("driverGratuity")
    ? convertNaN(resolvePercentItem(normalizedDriverGratuityParams.percent, normalizedDriverGratuityParams.amt, baseRateAmt))
    : null;
  const promoDiscountValue = resolveStoredOrFirstPassItem(
    "promoDiscount",
    promoDiscountPercent,
    promoDiscountAmt
  );
  const taxValue = resolveStoredOrFirstPassItem("tax", taxPercent, taxAmt);
  const tollsValue = convertNaN(tollsAmt);
  const otherValue = resolveStoredOrFirstPassItem(
    "other",
    otherPercent,
    otherAmt
  );
  const other2Value = resolveStoredOrFirstPassItem(
    "other2",
    other2Percent,
    other2Amt
  );
  const other3Value = resolveStoredOrFirstPassItem(
    "other3",
    other3Percent,
    other3Amt
  );
  const other4Value = resolveStoredOrFirstPassItem(
    "other4",
    other4Percent,
    other4Amt
  );
  const other5Value = resolveStoredOrFirstPassItem(
    "other5",
    other5Percent,
    other5Amt
  );
  const other6Value = resolveStoredOrFirstPassItem(
    "other6",
    other6Percent,
    other6Amt
  );
  const stcValue = resolveStoredOrFirstPassItem("stc", stcPercent, stcAmt);
  const creditCardServiceFeeValue = resolveStoredOrFirstPassItem(
    "creditCardServiceFee",
    creditCardServiceFeePercent,
    creditCardServiceFeeAmt
  );

  // Compute subtotal: baseRate + subtotal-group items (discounts subtracted)
  let subtotalAmt = baseRateAmt;
  if (hasCalcOnSubtotal) {
    if (!calcOnSubtotalSet.has("driverGratuity")) subtotalAmt += driverGratuityValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("promoDiscount")) subtotalAmt -= promoDiscountValue || 0;
    if (!calcOnSubtotalSet.has("tax")) subtotalAmt += taxValue || 0;
    if (!calcOnSubtotalSet.has("tolls")) subtotalAmt += tollsValue || 0;
    if (!calcOnSubtotalSet.has("meetGreet")) subtotalAmt += meetGreetAmtInput || 0;
    if (!calcOnSubtotalSet.has("other")) subtotalAmt += otherValue || 0;
    if (!calcOnSubtotalSet.has("other2")) subtotalAmt += other2Value || 0;
    if (!calcOnSubtotalSet.has("other3")) subtotalAmt += other3Value || 0;
    if (!calcOnSubtotalSet.has("other4")) subtotalAmt += other4Value || 0;
    if (!calcOnSubtotalSet.has("other5")) subtotalAmt += other5Value || 0;
    if (!calcOnSubtotalSet.has("other6")) subtotalAmt += other6Value || 0;
    if (!calcOnSubtotalSet.has("stc")) subtotalAmt += stcValue || 0;
    if (!calcOnSubtotalSet.has("creditCardServiceFee")) subtotalAmt += creditCardServiceFeeValue || 0;
  }

  // Second pass: resolve calculated-on-subtotal items using subtotalAmt
  const finalDriverGratuityValue = calcOnSubtotalSet.has("driverGratuity")
    ? convertNaN(resolvePercentItem(normalizedDriverGratuityParams.percent, normalizedDriverGratuityParams.amt, subtotalAmt))
    : driverGratuityValueFirstPass;
  const finalPromoDiscountValue = resolveStoredOrFinalItem(
    "promoDiscount",
    promoDiscountPercent,
    promoDiscountAmt,
    promoDiscountValue
  );
  const finalTaxValue = resolveStoredOrFinalItem(
    "tax",
    taxPercent,
    taxAmt,
    taxValue
  );
  const finalOtherValue = resolveStoredOrFinalItem(
    "other",
    otherPercent,
    otherAmt,
    otherValue
  );
  const finalOther2Value = resolveStoredOrFinalItem(
    "other2",
    other2Percent,
    other2Amt,
    other2Value
  );
  const finalOther3Value = resolveStoredOrFinalItem(
    "other3",
    other3Percent,
    other3Amt,
    other3Value
  );
  const finalOther4Value = resolveStoredOrFinalItem(
    "other4",
    other4Percent,
    other4Amt,
    other4Value
  );
  const finalOther5Value = resolveStoredOrFinalItem(
    "other5",
    other5Percent,
    other5Amt,
    other5Value
  );
  const finalOther6Value = resolveStoredOrFinalItem(
    "other6",
    other6Percent,
    other6Amt,
    other6Value
  );
  const finalStcValue = resolveStoredOrFinalItem(
    "stc",
    stcPercent,
    stcAmt,
    stcValue
  );
  const finalCreditCardServiceFeeValue = resolveStoredOrFinalItem(
    "creditCardServiceFee",
    creditCardServiceFeePercent,
    creditCardServiceFeeAmt,
    creditCardServiceFeeValue
  );

  const pricing = {
    baseRate: {
      label: renderBaseRateFieldLabel(hourlyBaseRate, hourlyBaseRateHours, currencyFormatter),
      value: baseRateAmt,
    },
    driverGratuity: {
      label: "Driver Gratuity",
      value: finalDriverGratuityValue,
    },
    promoDiscount: {
      label: "Discount",
      value: finalPromoDiscountValue,
      isNeg: true,
    },
    tax: {
      label: "Tax",
      value: finalTaxValue,
    },
    tolls: {
      label: "Tolls",
      value: tollsValue,
    },
    meetGreet: {
      label: "Meet & Greet",
      value: meetGreetAmtInput,
      selected: meetGreetSelected,
    },
    rearFacingSeat: {
      label:
        rearFacingSeatQuantity > 1
          ? `${rearFacingSeatQuantity} X Rear-Facing Seat`
          : "Rear-Facing Seat",
      value: rearFacingSeatAmt === 0 ? 0 : convertNaN(rearFacingSeatAmt),
      quantity: rearFacingSeatQuantity,
    },
    forwardFacingSeat: {
      label:
        forwardFacingSeatQuantity > 1
          ? `${forwardFacingSeatQuantity} X Forward-Facing Seat`
          : "Forward-Facing Seat",
      value: forwardFacingSeatAmt === 0 ? 0 : convertNaN(forwardFacingSeatAmt),
      quantity: forwardFacingSeatQuantity,
    },
    boosterSeat: {
      label:
        boosterSeatQuantity > 1
          ? `${boosterSeatQuantity} X Booster Seat`
          : "Booster Seat",
      value: boosterSeatAmt === 0 ? 0 : convertNaN(boosterSeatAmt),
      quantity: boosterSeatQuantity,
    },
    other: {
      label: otherName || "Other",
      value: finalOtherValue,
    },
    other2: {
      label: other2Name || "Other 2",
      value: finalOther2Value,
    },
    other3: {
      label: other3Name || "Other 3",
      value: finalOther3Value,
    },
    other4: {
      label: other4Name || "Other 4",
      value: finalOther4Value,
    },
    other5: {
      label: other5Name || "Other 5",
      value: finalOther5Value,
    },
    other6: {
      label: other6Name || "Other 6",
      value: finalOther6Value,
    },
    stc: {
      label: "Standard Transportation Costs",
      value: finalStcValue,
    },
    creditCardServiceFee: {
      label: "Credit Card Service Fee",
      value: finalCreditCardServiceFeeValue,
    },
    promoCode,
  };

  const totalAmt = convertNaN(
    pricing.baseRate.value +
      pricing.driverGratuity.value -
      pricing.promoDiscount.value +
      pricing.tax.value +
      pricing.tolls.value +
      pricing.meetGreet.value +
      pricing.boosterSeat.value +
      pricing.forwardFacingSeat.value +
      pricing.rearFacingSeat.value +
      pricing.other.value +
      pricing.other2.value +
      pricing.other3.value +
      pricing.other4.value +
      pricing.other5.value +
      pricing.other6.value +
      pricing.stc.value +
      pricing.creditCardServiceFee.value -
      pricing.promoCode.value
  );

  return {
    tripId: trip.id,
    pricing,
    totalAmt,
  };
};

export const adjustTripIndex = (pricings: CustomerPricingType[]) => {
  let currentIndex = 0;
  let previousHasReturn = false;
  return pricings.map((pricing) => {
    if (previousHasReturn) ++currentIndex;
    previousHasReturn = !!pricing.returnTripPricing;
    return {
      ...pricing,
      tripIndex: ++currentIndex,
    };
  });
};
